/**
 * Runtime mirror of scripts/seo-route-meta.mjs — keep keys in sync.
 * Used by DynamicOpenGraph / SeoVerticalBody so CSR matches prerender.
 */

export type SeoMetaSpec = {
  title: string;
  desc: string;
  ns?: string;
  titleKey?: string | null;
  descKey?: string | null;
  h1Key?: string;
  bodyKey?: string;
  fallbackTitleKey?: string;
  fallbackDescKey?: string;
  skinNs?: string;
  skinTitleKey?: string;
  skinDescKey?: string;
  fallbackSkinTitleKey?: string;
  fallbackSkinDescKey?: string;
};

export const PAGES: Record<string, SeoMetaSpec> = {
  home: {
    title: 'KOMBY — MetaSaaS for Restaurants, Offices & Nightlife',
    desc: 'One platform replaces 6-8 fragmented tools. Custom apps, AI agents, and automation for businesses worldwide.',
    ns: 'seo.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
  },
  pricing: {
    title: 'Pricing — Starter, Pro & Enterprise',
    desc: 'Compare Komby plans. Start free, scale as you grow. Transparent pricing, no hidden fees.',
    ns: 'pricing.json',
    titleKey: 'seo.pricingTitle',
    descKey: 'seo.pricingDescription',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
    fallbackTitleKey: 'title',
    fallbackDescKey: 'subtitle',
  },
  compare: {
    title: 'Compare Plans — Feature Matrix',
    desc: 'Side-by-side comparison of Komby Starter, Pro and Enterprise features.',
    ns: 'compare.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
  },
  team: {
    title: 'Our Team',
    desc: 'Meet the people building the future of MetaSaaS.',
    ns: 'team.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
    fallbackTitleKey: 'title',
    fallbackDescKey: 'subtitle',
  },
  music: {
    title: 'Komby Music',
    desc: 'The official soundtrack of MetaSaaS. Electronic pop from Hong Kong.',
    ns: 'music.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
  },
};

export const SKINS: Record<string, SeoMetaSpec> = {
  resto: {
    title: 'Komby for Restaurants',
    desc: 'All-in-one digital platform for restaurants. Reservations, menus, reviews, delivery.',
    ns: 'resto.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
  },
  office: {
    title: 'Komby for Offices',
    desc: 'Workspace management platform. Smart scheduling, room booking, hybrid work tools.',
    ns: 'office.json',
    titleKey: 'seo.title',
    descKey: 'seo.description',
    h1Key: 'seo.h1',
    bodyKey: 'seo.seoBody',
  },
  nightclub: {
    title: 'Komby for Nightlife',
    desc: 'Digital solutions for bars, clubs, and entertainment venues.',
    ns: 'home.json',
    titleKey: 'nightclub.seo.title',
    descKey: 'nightclub.seo.description',
    h1Key: 'nightclub.seo.h1',
    bodyKey: 'nightclub.seo.seoBody',
    fallbackTitleKey: 'nightclub.hero.title',
    fallbackDescKey: 'nightclub.hero.description',
  },
  'resto/pricing': {
    title: 'Restaurant Plans & Pricing',
    desc: 'Pricing plans tailored for restaurants. From single venue to multi-location.',
    skinNs: 'resto.json',
    skinTitleKey: 'seo.pricingTitle',
    skinDescKey: 'seo.pricingDescription',
    h1Key: 'seo.pricingH1',
    bodyKey: 'seo.pricingSeoBody',
    fallbackSkinTitleKey: 'seo.title',
    fallbackSkinDescKey: 'seo.description',
  },
  'office/pricing': {
    title: 'Office Plans & Pricing',
    desc: 'Pricing plans for workspace management. Flexible for any team size.',
    skinNs: 'office.json',
    skinTitleKey: 'seo.pricingTitle',
    skinDescKey: 'seo.pricingDescription',
    h1Key: 'seo.pricingH1',
    bodyKey: 'seo.pricingSeoBody',
    fallbackSkinTitleKey: 'seo.title',
    fallbackSkinDescKey: 'seo.description',
  },
  'nightclub/pricing': {
    title: 'Nightlife Plans & Pricing',
    desc: 'Nightlife venue pricing: events, VIP, guest lists, and entertainment ops — distinct from restaurant and office plans.',
    skinNs: 'home.json',
    skinTitleKey: 'nightclub.seo.pricingTitle',
    skinDescKey: 'nightclub.seo.pricingDescription',
    h1Key: 'nightclub.seo.pricingH1',
    bodyKey: 'nightclub.seo.pricingSeoBody',
    fallbackSkinTitleKey: 'nightclub.seo.title',
    fallbackSkinDescKey: 'nightclub.seo.description',
  },
};

export const PHASE1_SEO_CONTENT_PAGES = new Set([
  'home',
  'pricing',
  'team',
  'music',
  'resto',
  'office',
  'nightclub',
  'resto/pricing',
  'office/pricing',
  'nightclub/pricing',
]);

export function getMetaSpec(pageName: string): SeoMetaSpec | null {
  return PAGES[pageName] || SKINS[pageName] || null;
}

export function resolvePageNameFromPath(pathname: string): string {
  const parts = String(pathname || '/')
    .split('/')
    .filter(Boolean);

  let i = 0;
  if (parts[0] && /^[a-z]{2}(-[A-Za-z]{2})?$/i.test(parts[0])) {
    i = 1;
  }

  const rest = parts.slice(i);
  if (rest.length === 0) return 'home';

  const skin = ['resto', 'office', 'nightclub'].includes(rest[0]) ? rest[0] : null;
  if (skin) {
    if (rest[1] === 'pricing') return `${skin}/pricing`;
    if (rest.length === 1) return skin;
  }

  if (rest[0] === 'pricing') return 'pricing';
  if (rest[0] === 'team') return 'team';
  if (rest[0] === 'music') return 'music';
  if (rest[0] === 'compare' && rest[1]) return `compare/${rest[1]}`;
  if (rest[0] === 'compare') return 'compare';
  if (rest[0] === 'use-case' && rest[1]) return `use-case/${rest[1]}`;
  if (rest[0] === 'replace' && rest[1]) return `replace/${rest[1]}`;
  if (rest[0] === 'blog' && rest[1]) return `blog/${rest[1]}`;
  if (rest[0] === 'blog') return 'blog';
  if (rest[0] === 'contact') return 'contact';
  if (rest[0] === 'about') return 'about';
  if (rest[0] === 'press') return 'press';
  if (rest[0] === 'careers') return 'careers';
  if (rest[0] === 'help') return 'help';
  if (rest[0] === 'support') return 'support';
  if (rest[0] === 'cgv') return 'cgv';
  if (rest[0] === 'rgpd' || rest[0] === 'gdpr') return 'rgpd';
  if (rest[0] === 'ai-policy') return 'ai-policy';
  if (rest[0] === 'request') return 'request';

  return 'home';
}

export function formatPageTitle(title: string): string {
  if (!title) return 'KOMBY';
  if (/\bKomby\b/i.test(title)) return title;
  return `${title} | KOMBY`;
}

export function nsFileToI18n(nsFile?: string): string | null {
  if (!nsFile) return null;
  return nsFile.replace(/\.json$/, '');
}

/** Convert dotted key "seo.title" → i18next key path relative to namespace root */
export function toI18nKey(keyPath?: string | null): string | null {
  if (!keyPath) return null;
  // Namespaces already load the JSON root; keys in files are often under "seo.*"
  // For resto.json, titleKey is "seo.title" → t('seo.title', { ns: 'resto' })
  return keyPath;
}
